import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Plane } from '@react-three/drei';
import * as THREE from 'three';

// Custom shader for ocean waves
const oceanVertexShader = `
  uniform float uTime;
  uniform vec2 uMouse;
  varying vec2 vUv;
  varying float vElevation;

  void main() {
    vUv = uv;
    
    vec4 modelPosition = modelMatrix * vec4(position, 1.0);
    
    // Create multiple wave layers for realistic ocean effect
    float elevation = sin(modelPosition.x * 0.3 + uTime * 0.5) * 0.1;
    elevation += sin(modelPosition.x * 0.2 + modelPosition.z * 0.1 + uTime * 0.3) * 0.15;
    elevation += sin(modelPosition.x * 0.1 + modelPosition.z * 0.2 + uTime * 0.8) * 0.05;
    
    // Add mouse interaction for parallax effect
    float mouseInfluence = length(uMouse - modelPosition.xy) * 0.1;
    elevation += sin(mouseInfluence + uTime) * 0.02;
    
    modelPosition.z += elevation;
    vElevation = elevation;
    
    vec4 viewPosition = viewMatrix * modelPosition;
    vec4 projectedPosition = projectionMatrix * viewPosition;
    
    gl_Position = projectedPosition;
  }
`;

const oceanFragmentShader = `
  uniform float uTime;
  uniform vec3 uColorDeep;
  uniform vec3 uColorSurface;
  uniform vec3 uColorFoam;
  varying vec2 vUv;
  varying float vElevation;

  void main() {
    // Create depth-based color mixing
    float mixStrength = (vElevation + 0.2) * 2.0;
    mixStrength = smoothstep(0.0, 1.0, mixStrength);
    
    vec3 color = mix(uColorDeep, uColorSurface, mixStrength);
    
    // Add foam effect on wave peaks
    float foam = step(0.15, vElevation);
    color = mix(color, uColorFoam, foam * 0.3);
    
    // Add subtle animation to the surface
    float shimmer = sin(vUv.x * 20.0 + uTime * 2.0) * sin(vUv.y * 20.0 + uTime * 1.5);
    color += shimmer * 0.02;
    
    gl_FragColor = vec4(color, 0.8);
  }
`;

interface OceanMeshProps {
  mousePosition: { x: number; y: number };
}

const OceanMesh: React.FC<OceanMeshProps> = ({ mousePosition }) => {
  const meshRef = useRef<THREE.Mesh>(null);
  const { viewport } = useThree();

  const uniforms = useMemo(
    () => ({
      uTime: { value: 0 },
      uMouse: { value: new THREE.Vector2(0, 0) },
      uColorDeep: { value: new THREE.Color('#003366') },
      uColorSurface: { value: new THREE.Color('#0066cc') },
      uColorFoam: { value: new THREE.Color('#ffffff') },
    }),
    []
  );

  useFrame((state) => {
    if (meshRef.current) {
      uniforms.uTime.value = state.clock.elapsedTime;
      uniforms.uMouse.value.set(
        (mousePosition.x / window.innerWidth) * viewport.width - viewport.width / 2,
        -(mousePosition.y / window.innerHeight) * viewport.height + viewport.height / 2
      );
    }
  });

  return (
    <Plane
      ref={meshRef}
      args={[20, 20, 128, 128]}
      rotation={[-Math.PI / 2, 0, 0]}
      position={[0, -2, 0]}
    >
      <shaderMaterial
        vertexShader={oceanVertexShader}
        fragmentShader={oceanFragmentShader}
        uniforms={uniforms}
        transparent
        side={THREE.DoubleSide}
      />
    </Plane>
  );
};

const OceanWaves: React.FC = () => {
  const [mousePosition, setMousePosition] = React.useState({ x: 0, y: 0 });
  const canvasRef = useRef<HTMLCanvasElement>(null);

  React.useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      setMousePosition({ x: event.clientX, y: event.clientY });
    };

    // WebGL context lost handler
    const handleContextLost = (event: Event) => {
      event.preventDefault();
      console.warn('WebGL context lost, attempting recovery...');
    };

    const handleContextRestored = () => {
      console.log('WebGL context restored');
    };

    window.addEventListener('mousemove', handleMouseMove);
    
    // Add WebGL context event listeners
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.addEventListener('webglcontextlost', handleContextLost);
      canvas.addEventListener('webglcontextrestored', handleContextRestored);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (canvas) {
        canvas.removeEventListener('webglcontextlost', handleContextLost);
        canvas.removeEventListener('webglcontextrestored', handleContextRestored);
      }
    };
  }, []);

  return (
    <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: -1 }}>
      <Canvas
        ref={canvasRef}
        camera={{ position: [0, 5, 5], fov: 75 }}
        style={{ background: 'linear-gradient(180deg, #001122 0%, #003366 50%, #0066cc 100%)' }}
        onCreated={({ gl }) => {
          // Configure WebGL context for better stability
          gl.setPixelRatio(Math.min(window.devicePixelRatio, 2));
          gl.setSize(window.innerWidth, window.innerHeight);
        }}
      >
        <ambientLight intensity={0.4} />
        <directionalLight position={[10, 10, 5]} intensity={0.6} color="#ffffff" />
        <directionalLight position={[-10, -10, -5]} intensity={0.2} color="#0099ff" />
        <OceanMesh mousePosition={mousePosition} />
        <fog attach="fog" args={['#003366', 8, 20]} />
      </Canvas>
    </div>
  );
};

export default OceanWaves;
